import ArticleBody_HowTrtPlusAnalyzesBloodwork from "./how-trt-plus-analyzes-your-bloodwork.jsx";
import ArticleBody_TrackingABadProtocol from "./tracking-a-bad-protocol-is-still-a-bad-protocol.jsx";
import ArticleBody_BuiltHereFirst from "./built-here-first.jsx";
import ArticleBody_TheGrowthAxis from "./the-growth-axis.jsx";
import ArticleBody_TheOverallScore from "./the-overall-score.jsx";
import ArticleBody_UnderstandingDoseResponse from "./understanding-dose-response.jsx";
import ArticleBody_GettingTheMostFromSupplements from "./getting-the-most-from-supplements.jsx";
import ArticleBody_LiveTestosteroneReadout from "./live-testosterone-readout.jsx";
import { BLOG_META } from "../blogMeta";

/*
 * Blog manifest for the React app. Merges the pure-JS BLOG_META (used
 * by Node build scripts) with per-post JSX Component references + rich
 * FAQ/related data that only React needs.
 */

// Per-slug additions the React app needs — Component, FAQ, related links.
const COMPONENT_MAP = {
    "live-testosterone-readout": {
        Component: ArticleBody_LiveTestosteroneReadout,
        faq: [],
        related: [],
    },
    "how-trt-plus-analyzes-your-bloodwork": {
        Component: ArticleBody_HowTrtPlusAnalyzesBloodwork,
        faq: [
            {
                q: "How does TRT Plus use AI?",
                a: "Only for interpretation and language. AI takes the engine's finished, structured result and writes it up as your report. The scoring, the personalized ranges, and the clinical logic are the engine's job, and that part is deterministic.",
            },
            {
                q: "How is this different from asking a chatbot to read my labs?",
                a: "A general model gives you a fresh interpretation each time and leans on population reference ranges by default. TRT Plus fixes the ranges and the scoring in the engine first — so the analysis is personalized and repeatable — then uses AI to write it up. The difference isn't the AI; it's the engineered model in front of it.",
            },
            {
                q: "Can I see how the engine works?",
                a: "Yes. The schematic, the formulas, and the cited studies behind each model are laid out in the app and on the engine page.",
            },
            {
                q: "Does this replace my healthcare provider?",
                a: "No. It gives you a consistent, contextualized read so the conversation with your healthcare provider starts from solid ground. It doesn't make the decision for you.",
            },
            {
                q: "Do other TRT apps do this?",
                a: "Most apps advertising \"AI bloodwork analysis\" are wrapping the same general model you already have access to. The engine is the hard part, and it's the part that's missing. A deterministic framework built on real-world TRT clinic data is what actually turns your labs into a personalized, repeatable read.",
            },
            {
                q: "Can I continue the discussion with my own AI?",
                a: "Yes. A hand-off button packages your full report, your lab markers, and the framework and model context behind them, the cascade and growth axis included, and shares it to your installed Claude, ChatGPT, or Grok so you can keep exploring. This is the opposite of dumping raw labs into a chatbot: you're handing the general model the engine's finished, structured analysis along with the framework it was built on, so the conversation stays anchored to a deterministic read rather than the model improvising from scratch.",
            },
        ],
        related: [],
    },
    "tracking-a-bad-protocol-is-still-a-bad-protocol": {
        Component: ArticleBody_TrackingABadProtocol,
        faq: [
            {
                q: "Does TRT Plus have dose logging and reminders?",
                a: "Yes, for testosterone and all of your compounds. The value is in what comes before logging: designing a protocol that's actually worth tracking.",
            },
            {
                q: "What does the Testimator do that a tracker doesn't?",
                a: "A tracker records what already happened. The Testimator models what your levels would look like on a protocol you haven't run yet, anchored to your own bloodwork, so you can dial one in before committing to it.",
            },
            {
                q: "Why not just use the reference ranges on my lab report?",
                a: "Those ranges describe the general population, not someone optimizing on TRT. Applied routinely they mislead, flagging expected-on-protocol values as problems and optimal-but-not-average values as fine. TRT Plus scores against enhanced ranges built from real-world TRT and enhanced cases instead.",
            },
            {
                q: "Does this replace my healthcare provider?",
                a: "No. Protocol and dosing decisions should always be made with your healthcare provider. TRT Plus helps you interpret your protocol and bloodwork together, so the conversation starts from solid ground.",
            },
        ],
        related: [],
    },
    "built-here-first": {
        Component: ArticleBody_BuiltHereFirst,
        faq: [
            {
                q: "Is the methodology actually documented?",
                a: "Yes. The models, formulas, and cited studies are published on the engine page, including the multi-route PK model, the A:E Androgenic Load formula, and the Cardio-Metabolic Cascade.",
            },
            {
                q: "Can clinicians and coaches use it with clients?",
                a: "TRT Plus is a consumer app, and its output is designed to inform the conversation with a healthcare provider, not replace it.",
            },
            {
                q: "Who is the developer?",
                a: "Nathan, founder and developer @ TRT Plus, designed, built, and maintains all of it: a career that began in pharmaceuticals and ran through two decades of financial-systems engineering.",
            },
        ],
        related: [],
    },
    "the-growth-axis": {
        Component: ArticleBody_TheGrowthAxis,
        faq: [
            {
                q: "Why doesn't TRT Plus measure growth hormone?",
                a: "Growth hormone has an extremely short half life and is gone within minutes, so a blood test can't reliably catch it. IGF-1 is the stable, long-lived driver it converts into, so the engine reads IGF-1 as the proxy for the whole axis.",
            },
            {
                q: "I run GH, but my IGF-1 is still low — why?",
                a: "Estradiol may be the limiting step. E2 enables the liver's conversion of growth hormone into IGF-1, so when it's low, IGF-1 can read low despite a high GH dose. It is worth leveraging E2 (via T) before adding more growth hormone.",
            },
            {
                q: "Does a low HbA1c help my IGF-1 range?",
                a: "Yes. Good glucose control is treated as permission: it shows you can better tolerate a higher IGF-1, so your range extends upward. HbA1c is assessed on the high side only, so good control extends the range while a low reading never counts against you.",
            },
        ],
        related: [],
    },
    "the-overall-score": {
        Component: ArticleBody_TheOverallScore,
        faq: [],
        related: [],
    },
    "understanding-dose-response": {
        Component: ArticleBody_UnderstandingDoseResponse,
        faq: [
            {
                q: "Can I compare my level to another guy's on the same dose?",
                a: "A different ester, a different injection frequency, and a different draw time mean you are comparing different quantities. Both readings have to be put on a common basis first, which is exactly what a responder type does.",
            },
            {
                q: "My responder type came back low. Do I need more testosterone?",
                a: "A low responder type does not mean your protocol needs adjusting. It just means you needed more testosterone than an efficient responder to get into the TRT-calibrated zone, and if you are there, you are fine. If your level itself is below target, rule out under-dosed product, poor injection technique, and an unsettled protocol before adding milligrams.",
            },
        ],
        related: [],
    },
    "getting-the-most-from-supplements": {
        Component: ArticleBody_GettingTheMostFromSupplements,
        faq: [
            {
                q: "Why do some supplements need weeks before they work?",
                a: "Because the useful marker for many of them is not the blood level but a tissue pool. Creatine fills your muscle creatine pool, beta-alanine fills muscle carnosine, fish oil fills membrane stores, vitamin D fills 25-OH-D storage. Those pools take weeks to months of daily dosing to reach steady state, even though the plasma clears in hours.",
            },
            {
                q: "When should I actually judge a supplement is working?",
                a: "Roughly four weeks for creatine and beta-alanine, about six weeks for magnesium glycinate's durable sleep benefit, two months for vitamin D, and four months for fish oil. NAC is the exception: full effect within a day, so timing around the event matters more than accumulation.",
            },
            {
                q: "Why doesn't loading creatine skip the wait entirely?",
                a: "Loading with 25 to 30 g a day fills the muscle pool faster than 5 g a day, but it also causes gastrointestinal upset. Taking 5 g a day and waiting about four weeks reaches the same saturation without the side effects.",
            },
            {
                q: "Does TRT Plus tell me when each supplement has landed?",
                a: "Yes. For supplements that accumulate in tissue, TRT Plus plots the curve against the pool that carries the effect and marks the date each milestone lands on the timeline: muscle saturation for creatine, muscle carnosine for beta-alanine, membrane stores for fish oil, 25-OH-D storage for vitamin D.",
            },
        ],
        related: [],
    },
};

export const BLOG_POSTS = BLOG_META.map((meta) => ({
    ...meta,
    ...COMPONENT_MAP[meta.slug],
}));

export function getPostBySlug(slug) {
    return BLOG_POSTS.find((p) => p.slug === slug) || null;
}
